import React, { useState, useRef } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  IconButton,
  TextField,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Divider,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  ShoppingCart as CartIcon,
} from '@mui/icons-material';
import { Plate, Business } from '../types';
import PlateService from '../services/plateService';
import ImageCarousel from './ImageCarousel';
import { useCart } from '../contexts/CartContext';
import RatingDisplay from './RatingDisplay';
import RatingComponent from './RatingComponent';
import { Rating } from '../types/rating';
import { useAuth } from '../contexts/AuthContext';
import { Snackbar } from '@mui/material';
import DatePickerDialog from './DatePickerDialog';
import PlateDishSelector from './PlateDishSelector';
import { API_BASE_URL } from '../config/apiConfig';

interface PlateCardProps {
  plate: Plate;
  business?: Business;
  onEdit: (plate: Plate) => void;
  onDelete: (plateId: string) => void;
  onUpdate: (plate: Plate) => void;
  onBuyNow?: (plate: Plate, business: Business) => void;
  showCartButton?: boolean;
  showBuyNowButton?: boolean;
}

const PlateCard: React.FC<PlateCardProps> = ({
  plate,
  business,
  onEdit,
  onDelete,
  onUpdate,
  onBuyNow,
  showCartButton = true,
  showBuyNowButton = true,
}) => {
  const [isEditingPrice, setIsEditingPrice] = useState(false);
  const [editPrice, setEditPrice] = useState(plate.price.toString());
  const [priceUpdateLoading, setPriceUpdateLoading] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [ratingDialogOpen, setRatingDialogOpen] = useState(false);
  const [existingRating, setExistingRating] = useState<Rating | undefined>(undefined);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({ open: false, message: '', severity: 'success' });
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const [quantitySelectorOpen, setQuantitySelectorOpen] = useState(false);
  const [selectedQuantity, setSelectedQuantity] = useState<number>(1);
  const [dishSelectorOpen, setDishSelectorOpen] = useState(false);
  const [pendingCartAction, setPendingCartAction] = useState<'add' | 'buyNow' | null>(null);
  const [pendingBookingDate, setPendingBookingDate] = useState<string | undefined>(undefined);
  const openingDishSelectorRef = useRef(false); // Track if we're about to open dish selector
  const { addToCart, isInCart, removeFromCart } = useCart();
  const { user } = useAuth();

  // Normalize plate image URL - handle Base64, file paths, and LOB OIDs
  const getPlateImageUrl = (imagePath: string | undefined): string => {
    if (!imagePath) return '';
    
    const baseUrl = API_BASE_URL.replace(/\/$/, '');
    
    // If it's Base64, return as is
    if (imagePath.startsWith('data:')) {
      return imagePath;
    }
    
    // If it's a full URL but contains localhost, replace with current backend URL
    if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
      // Check if it's a localhost URL (localhost, 127.0.0.1, or any localhost with port)
      if (imagePath.includes('localhost') || imagePath.includes('127.0.0.1')) {
        // Extract the path part and use current backend URL
        try {
          const url = new URL(imagePath);
          const path = url.pathname;
          return `${baseUrl}${path}`;
        } catch (e) {
          // If URL parsing fails, try to extract path manually
          const pathMatch = imagePath.match(/\/uploads\/.*/);
          if (pathMatch) {
            return `${baseUrl}${pathMatch[0]}`;
          }
        }
      }
      // If it's a valid external URL (not localhost), return as is
      return imagePath;
    }
    
    // If it's just a number (LOB OID), it's invalid - return empty or fallback
    if (/^\d+$/.test(imagePath.trim())) {
      console.warn('Plate image is stored as LOB OID, not a valid image:', imagePath);
      return ''; // Return empty to show no image
    }
    
    // If it's a relative path starting with /uploads, prepend the backend URL
    if (imagePath.startsWith('/uploads/')) {
      return `${baseUrl}${imagePath}`;
    }
    
    // If it's just a filename or path without leading slash, construct the full path
    if (imagePath.includes('uploads/') || imagePath.includes('plates/')) {
      const normalizedPath = imagePath.startsWith('/') ? imagePath : `/${imagePath}`;
      return `${baseUrl}${normalizedPath}`;
    }
    
    // Default: assume it's a relative path in uploads/plates
    return `${baseUrl}/uploads/plates/${imagePath}`;
  };

  const handleCartToggle = (event?: React.MouseEvent) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    
    try {
      if (!business) {
        alert('Error: Business information is missing. Cannot add to cart.');
        return;
      }
      
      if (!plate || !plate.plateId) {
        alert('Error: Plate information is missing. Cannot add to cart.');
        return;
      }
      
      const inCart = isInCart(plate.plateId, 'plate');
      
      if (inCart) {
        removeFromCart(plate.plateId, 'plate');
      } else {
        // Open date picker first
        setPendingCartAction('add');
        setDatePickerOpen(true);
      }
    } catch (error) {
      alert('Error adding item to cart. Please try again.');
    }
  };


  const handleDateConfirm = (date: string | undefined) => {
    if (!business) {
      return;
    }
    
    if (!pendingCartAction) {
      return;
    }
    
    // Store the booking date
    setPendingBookingDate(date);
    
    // Close date picker and open quantity selector
    setDatePickerOpen(false);
    setError(null); // Clear any previous errors
    setSelectedQuantity(1);
    setTimeout(() => {
      setQuantitySelectorOpen(true);
    }, 0);
  };

  const handleQuantityConfirm = () => {
    if (!business) {
      console.error('Business is missing in handleQuantityConfirm');
      setError('Business information is missing. Please try again.');
      return;
    }
    
    // If pendingCartAction is null, default to 'add' (shouldn't happen, but handle gracefully)
    const action = pendingCartAction || 'add';
    
    console.log('handleQuantityConfirm called:', {
      action,
      selectedQuantity,
      pendingBookingDate,
      businessCategory: business.businessCategory
    });
    
    // Close quantity selector
    setQuantitySelectorOpen(false);
    
    // For catering businesses, open dish selector next
    if (business.businessCategory === 'caters') {
      openingDishSelectorRef.current = true;
      setTimeout(() => {
        setDishSelectorOpen(true);
        openingDishSelectorRef.current = false;
      }, 0);
    } else {
      // For non-catering businesses, add directly to cart with quantity
      try {
        addToCart(plate, business, pendingBookingDate, undefined, selectedQuantity);
        // Show success message
        setSnackbar({
          open: true,
          message: `${selectedQuantity} plate${selectedQuantity !== 1 ? 's' : ''} added to cart successfully!`,
          severity: 'success'
        });
      } catch (error: any) {
        console.error('Error adding to cart:', error);
        const errorMessage = error?.message || 'Failed to add to cart. Please try again.';
        setError(errorMessage);
        setSnackbar({
          open: true,
          message: errorMessage,
          severity: 'error'
        });
        // Re-open quantity selector so user can try again
        setTimeout(() => {
          setQuantitySelectorOpen(true);
        }, 100);
        return; // Don't reset states if there was an error
      }
      
      if (action === 'buyNow') {
        if (onBuyNow) {
          onBuyNow(plate, business);
        }
      }
      
      // Reset states only on success
      setPendingCartAction(null);
      setPendingBookingDate(undefined);
      setSelectedQuantity(1);
    }
  };

  const handleDishSelectorConfirm = (selectedDishes: Array<{ dishId: string; dishName: string; dishPrice: number; quantity: number }>) => {
    if (!business) {
      console.error('Business is missing in handleDishSelectorConfirm');
      setError('Business information is missing. Please try again.');
      return;
    }
    
    // If pendingCartAction is null, default to 'add' (shouldn't happen, but handle gracefully)
    const action = pendingCartAction || 'add';
    
    console.log('handleDishSelectorConfirm called:', {
      action,
      selectedQuantity,
      selectedDishesCount: selectedDishes.length,
      pendingBookingDate,
      businessCategory: business.businessCategory
    });
    
    // Add to cart with selected date, dishes, and quantity
    // Note: selectedDishes can be empty array if user didn't select any optional dishes
    try {
      addToCart(plate, business, pendingBookingDate, selectedDishes, selectedQuantity);
      
      // Show success message
      const dishMessage = selectedDishes.length > 0 
        ? ` with ${selectedDishes.length} dish${selectedDishes.length !== 1 ? 'es' : ''}`
        : '';
      setSnackbar({
        open: true,
        message: `${selectedQuantity} plate${selectedQuantity !== 1 ? 's' : ''}${dishMessage} added to cart successfully!`,
        severity: 'success'
      });
    } catch (error: any) {
      console.error('Error adding to cart:', error);
      const errorMessage = error?.message || 'Failed to add to cart. Please try again.';
      setError(errorMessage);
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error'
      });
      // Re-open dish selector so user can try again
      setTimeout(() => {
        setDishSelectorOpen(true);
      }, 100);
      return; // Don't reset states if there was an error
    }
    
    if (action === 'buyNow') {
      // Call parent's onBuyNow if provided
      if (onBuyNow) {
        onBuyNow(plate, business);
      }
    }
    
    // Reset states only on success
    setDishSelectorOpen(false);
    setPendingCartAction(null);
    setPendingBookingDate(undefined);
    setSelectedQuantity(1);
  };

  const handleQuantitySelectorCancel = () => {
    setQuantitySelectorOpen(false);
    // Only reset pendingCartAction if we're not about to open dish selector
    if (!openingDishSelectorRef.current) {
      setPendingCartAction(null);
      setPendingBookingDate(undefined);
    }
    setSelectedQuantity(1);
  };

  const handleDishSelectorCancel = () => {
    setDishSelectorOpen(false);
    setPendingCartAction(null);
    setPendingBookingDate(undefined);
    setSelectedQuantity(1);
  };



  const handlePriceCancel = () => {
    setIsEditingPrice(false);
    setEditPrice(plate.price.toString());
  };

  const handlePriceSave = async () => {
    const newPrice = parseFloat(editPrice);
    if (isNaN(newPrice) || newPrice <= 0) {
      setError('Please enter a valid price');
      return;
    }

    try {
      setPriceUpdateLoading(true);
      setError(null);
      
      // Update plate with new price, passing vendor phone for authorization
      const updatedPlate = await PlateService.updatePlate(
        plate.plateId, 
        {
          businessId: plate.businessId,
          dishName: plate.dishName,
          dishDescription: plate.dishDescription,
          plateImage: plate.plateImage,
          price: newPrice,
          dishType: plate.dishType || 'veg',
        },
        user?.phoneNumber
      );
      
      // Update parent component with the server response
      onUpdate(updatedPlate);
      setIsEditingPrice(false);
    } catch (err: any) {
      setError('Failed to update price');
      console.error('Error updating plate price:', err);
    } finally {
      setPriceUpdateLoading(false);
    }
  };

  const handleDeleteConfirm = async () => {
    try {
      await PlateService.deletePlate(plate.plateId, user?.phoneNumber);
      onDelete(plate.plateId);
      setDeleteDialogOpen(false);
    } catch (err: any) {
      setError(err.message || 'Failed to delete plate');
      console.error('Error deleting plate:', err);
    }
  };

  const handleRateClick = () => {
    setRatingDialogOpen(true);
  };

  const handleRatingSubmitted = (rating: Rating) => {
    setExistingRating(rating);
    setRatingDialogOpen(false);
  };

  const handleRatingDialogClose = () => {
    setRatingDialogOpen(false);
  };

  return (
    <>
      <Card sx={{ 
        maxWidth: { xs: '100%', sm: 345 }, 
        height: '100%', 
        display: 'flex', 
        flexDirection: 'column',
        width: '100%',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        position: 'relative',
        overflow: 'hidden',
        background: 'linear-gradient(135deg, #ffffff 0%, #f0fff4 100%)',
        border: '2px solid transparent',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'linear-gradient(135deg, rgba(67, 233, 123, 0.1) 0%, rgba(77, 150, 255, 0.1) 50%, rgba(255, 217, 61, 0.1) 100%)',
          opacity: 0,
          transition: 'opacity 0.3s',
          zIndex: 0,
          pointerEvents: 'none',
        },
        '&:hover': {
          transform: 'translateY(-8px) scale(1.02)',
          boxShadow: '0 12px 40px rgba(67, 233, 123, 0.3), 0 0 20px rgba(77, 150, 255, 0.2)',
          border: '2px solid',
          borderImage: 'linear-gradient(135deg, #43e97b, #4D96FF, #FFD93D) 1',
          '&::before': {
            opacity: 1,
          },
        },
      }}>
        {plate.plateImage && (
          <Box sx={{ 
            height: { xs: 'clamp(200px, 40vh, 280px)', sm: 'clamp(250px, 35vh, 320px)', md: 'clamp(280px, 35vh, 350px)' },
            width: '100%',
            position: 'relative',
            overflow: 'hidden',
            '& > div': {
              height: '100% !important',
              width: '100% !important'
            },
            '& img': {
              objectFit: 'cover',
              width: '100%',
              height: '100%'
            }
          }}>
            <ImageCarousel
              images={[{
                imageId: plate.plateId,
                imageUrl: getPlateImageUrl(plate.plateImage),
                imageName: plate.dishName
              }]}
              height={400}
              showNavigation={false}
              showDots={false}
            />
          </Box>
        )}
        <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
          {/* Chips Section - Veg/Non-Veg and Active/Inactive */}
          <Box 
            sx={{ 
              display: 'flex', 
              justifyContent: 'flex-start',
              alignItems: 'center',
              mb: 1,
              gap: { xs: 0.5, sm: 1 },
              flexWrap: 'wrap'
            }}
          >
            <Chip 
              label={(plate.dishType || 'veg') === 'veg' ? 'Veg' : 'Non-Veg'} 
              color={(plate.dishType || 'veg') === 'veg' ? 'success' : 'error'}
              size="small"
              variant="outlined"
              sx={{
                fontSize: { xs: 'clamp(0.625rem, 1vw, 0.75rem)', sm: '0.75rem' },
                height: { xs: 'clamp(20px, 3vh, 24px)', sm: '24px' }
              }}
            />
            <Chip 
              label={plate.isActive ? 'Active' : 'Inactive'} 
              color={plate.isActive ? 'success' : 'default'}
              size="small"
              sx={{
                fontSize: { xs: 'clamp(0.625rem, 1vw, 0.75rem)', sm: '0.75rem' },
                height: { xs: 'clamp(20px, 3vh, 24px)', sm: '24px' }
              }}
            />
          </Box>
          
          {/* Dish Name - Below the chips */}
          <Typography 
            variant="h6" 
            component="h2" 
            sx={{ 
              fontWeight: 'bold',
              mb: 1,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              wordBreak: 'break-word',
              fontSize: { xs: 'clamp(1rem, 2vw, 1.125rem)', sm: 'clamp(1.125rem, 2vw, 1.25rem)' },
              lineHeight: { xs: 'clamp(1.25rem, 2.5vw, 1.5rem)', sm: 'clamp(1.375rem, 2.5vw, 1.625rem)' }
            }}
          >
            {plate.dishName}
          </Typography>
          
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2, flexGrow: 1 }}>
            {plate.dishDescription}
          </Typography>
          
          {/* Price Section */}
          <Box sx={{ mb: 2 }}>
            {isEditingPrice ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <TextField
                  size="small"
                  type="number"
                  value={editPrice}
                  onChange={(e) => setEditPrice(e.target.value)}
                  inputProps={{ min: 0, step: 0.01 }}
                  sx={{ 
                    width: { xs: 'clamp(80px, 20vw, 120px)', sm: 120 },
                    flexGrow: 1,
                    '& .MuiInputBase-input': {
                      fontSize: { xs: 'clamp(0.875rem, 1.5vw, 1rem)', sm: '1rem' }
                    }
                  }}
                />
                <IconButton
                  size="small"
                  color="primary"
                  onClick={handlePriceSave}
                  disabled={priceUpdateLoading}
                  sx={{ padding: { xs: 'clamp(4px, 1vw, 8px)', sm: 1 } }}
                >
                  <SaveIcon sx={{ fontSize: { xs: 'clamp(16px, 2vw, 18px)', sm: 18 } }} />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={handlePriceCancel}
                  disabled={priceUpdateLoading}
                  sx={{ padding: { xs: 'clamp(4px, 1vw, 8px)', sm: 1 } }}
                >
                  <CancelIcon sx={{ fontSize: { xs: 'clamp(16px, 2vw, 18px)', sm: 18 } }} />
                </IconButton>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, flexWrap: 'wrap', gap: 1 }}>
                <Typography 
                  variant="h6" 
                  color="primary" 
                  sx={{ 
                    fontWeight: 'bold',
                    fontSize: { xs: 'clamp(1rem, 2vw, 1.25rem)', sm: 'clamp(1.125rem, 2vw, 1.5rem)' }
                  }}
                >
                  ₹{plate.price.toFixed(2)}
                </Typography>
              </Box>
            )}
          </Box>
            
          {/* Rating Display */}
          {business && (
            <Box 
              mt={1} 
              mb={2}
              sx={{
                '& .MuiRating-root': {
                  fontSize: { xs: 'clamp(14px, 1.5vw, 16px)', sm: 'clamp(16px, 1.5vw, 18px)' }
                },
                '& .MuiTypography-root': {
                  fontSize: { xs: 'clamp(0.625rem, 1vw, 0.7rem)', sm: 'clamp(0.7rem, 1vw, 0.8rem)' }
                },
                '& .MuiButton-root': {
                  fontSize: { xs: 'clamp(0.625rem, 1vw, 0.7rem)', sm: 'clamp(0.7rem, 1vw, 0.8rem)' },
                  padding: { xs: 'clamp(2px, 0.5vw, 4px) clamp(6px, 1vw, 8px)', sm: '4px 8px' },
                  minHeight: { xs: 'clamp(20px, 3vh, 24px)', sm: '24px' }
                },
                '& .MuiSvgIcon-root': {
                  fontSize: { xs: 'clamp(14px, 1.5vw, 16px)', sm: 'clamp(16px, 1.5vw, 18px)' }
                }
              }}
            >
              <RatingDisplay
                itemId={plate.plateId}
                itemType="PLATE"
                businessId={business.businessId}
                onRateClick={handleRateClick}
                showRateButton={true}
                compact={true}
              />
            </Box>
          )}
          
          {/* Cart Button - Only show Add to Cart for clients, no Buy Now */}
          {business && showCartButton && user?.userType === 'CLIENT' && (
            <Box sx={{ mb: 2 }}>
              <Button
                variant={isInCart(plate.plateId, 'plate') ? "contained" : "outlined"}
                color={isInCart(plate.plateId, 'plate') ? "success" : "primary"}
                size="small"
                startIcon={<CartIcon sx={{ fontSize: { xs: 'clamp(14px, 1.5vw, 16px)', sm: 'clamp(16px, 1.5vw, 18px)' } }} />}
                onClick={(e) => {
                  handleCartToggle(e);
                }}
                disabled={!business || !plate || !plate.plateId}
                fullWidth
                sx={{
                  fontSize: { xs: 'clamp(0.625rem, 1vw, 0.7rem)', sm: 'clamp(0.7rem, 1vw, 0.8rem)' },
                  padding: { xs: 'clamp(6px, 1vw, 8px) clamp(12px, 2vw, 16px)', sm: 'clamp(8px, 1vw, 10px) clamp(16px, 2vw, 20px)' },
                  minHeight: { xs: 'clamp(32px, 4vh, 36px)', sm: 'clamp(36px, 5vh, 40px)' },
                  whiteSpace: 'nowrap',
                  textOverflow: 'ellipsis',
                  overflow: 'hidden',
                  pointerEvents: 'auto',
                  zIndex: 1
                }}
              >
                {isInCart(plate.plateId, 'plate') ? "In Cart" : "Add to Cart"}
              </Button>
            </Box>
          )}

          {/* Admin Action Buttons - Only show for vendors */}
          {user && user.userType === 'VENDOR' && (
            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
              <IconButton
                size="small"
                color="primary"
                onClick={(e) => {
                  e.stopPropagation();
                  onEdit(plate);
                }}
                title="Edit Plate"
                sx={{ padding: { xs: 'clamp(4px, 1vw, 8px)', sm: 1 } }}
              >
                <EditIcon sx={{ fontSize: { xs: 'clamp(16px, 2vw, 18px)', sm: 18 } }} />
              </IconButton>
              <IconButton
                size="small"
                color="error"
                onClick={(e) => {
                  e.stopPropagation();
                  setDeleteDialogOpen(true);
                }}
                title="Delete Plate"
                sx={{ padding: { xs: 'clamp(4px, 1vw, 8px)', sm: 1 } }}
              >
                <DeleteIcon sx={{ fontSize: { xs: 'clamp(16px, 2vw, 18px)', sm: 18 } }} />
              </IconButton>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Plate</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{plate.dishName}"? This action cannot be undone.
          </Typography>
          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Rating Dialog */}
      {business && (
        <RatingComponent
          open={ratingDialogOpen}
          onClose={handleRatingDialogClose}
          itemId={plate.plateId}
          itemType="PLATE"
          itemName={plate.dishName}
          businessId={business.businessId}
          existingRating={existingRating}
          onRatingSubmitted={handleRatingSubmitted}
        />
      )}
      
      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Date Picker Dialog */}
      {business && (
        <>
          <DatePickerDialog
            open={datePickerOpen}
            onClose={() => {
              // Only reset pendingCartAction if we're NOT opening the dish selector
              // If dish selector is about to open, we need to keep pendingCartAction
              // We'll reset it when dish selector is cancelled or confirmed
              if (!openingDishSelectorRef.current) {
                setPendingCartAction(null);
                setPendingBookingDate(undefined);
              }
              setDatePickerOpen(false);
            }}
            onConfirm={handleDateConfirm}
            itemId={plate.plateId}
            itemType="plate"
            itemName={plate.dishName}
          businessId={business.businessId}
          title="Select Booking Date for Plate"
          onNotify={(date) => {
            setSnackbar({ 
              open: true, 
              message: `You will be notified when "${plate.dishName}" becomes available on ${date}`, 
              severity: 'success' 
            });
          }}
        />
        <PlateDishSelector
          open={dishSelectorOpen}
          onClose={handleDishSelectorCancel}
          onConfirm={handleDishSelectorConfirm}
          plate={plate}
          business={business}
        />
        
        {/* Quantity Selector Dialog */}
        <Dialog 
          open={quantitySelectorOpen} 
          onClose={(event, reason) => {
            // Only allow closing via backdrop click or escape if there's no pending action
            if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
              handleQuantitySelectorCancel();
            }
          }}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Select Quantity</DialogTitle>
          <DialogContent>
            <Box sx={{ py: 2 }}>
              {error && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                  {error}
                </Alert>
              )}
              <Typography variant="h6" gutterBottom>
                {plate.dishName}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Price per plate: ₹{plate.price.toFixed(2)}
              </Typography>
              
              <TextField
                fullWidth
                type="number"
                label="Quantity"
                value={selectedQuantity}
                onChange={(e) => {
                  const value = parseInt(e.target.value) || 1;
                  setSelectedQuantity(Math.max(1, value));
                }}
                inputProps={{ 
                  min: 1,
                  step: 1
                }}
                sx={{ mb: 3 }}
                helperText="Enter the number of plates you need"
              />
              
              <Box sx={{ 
                p: 2, 
                bgcolor: 'background.paper', 
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'divider'
              }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body1">
                    Quantity:
                  </Typography>
                  <Typography variant="body1" fontWeight="bold">
                    {selectedQuantity} plate{selectedQuantity !== 1 ? 's' : ''}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body1">
                    Price per plate:
                  </Typography>
                  <Typography variant="body1">
                    ₹{plate.price.toFixed(2)}
                  </Typography>
                </Box>
                <Divider sx={{ my: 1 }} />
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="h6" fontWeight="bold">
                    Total Price:
                  </Typography>
                  <Typography variant="h6" color="primary" fontWeight="bold">
                    ₹{(plate.price * selectedQuantity).toFixed(2)}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleQuantitySelectorCancel}>
              Cancel
            </Button>
            <Button 
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                console.log('Continue button clicked');
                handleQuantityConfirm();
              }} 
              variant="contained" 
              color="primary"
              disabled={!business || selectedQuantity < 1}
            >
              Continue
            </Button>
          </DialogActions>
        </Dialog>
        </>
      )}
    </>
  );
};

export default PlateCard;
